import type { LocaleCode, MatchType, ResolvedLocale } from './types';
import { canonicalizeLocale } from './canonicalize';

/**
 * Extract the BCP 47 script subtag (`Hans`, `Hant`, `Cyrl`, `Latn`, ...) from
 * a canonical locale code, or return null if none is present. Script subtags
 * are 4-letter subtags past position 0 — regions are 2 letters or 3 digits,
 * variants are 5-8 characters, so a 4-letter subtag can only be a script.
 * Assumes the input is already canonical (script title-cased, region upper).
 */
function extractScript(canonical: string): string | null {
  const parts = canonical.split('-');
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].length === 4) return parts[i];
  }
  return null;
}

/**
 * Build a locale resolver bound to a specific (supportedLocales, fallbackLocale)
 * pair. The canonical lookup map is constructed ONCE at factory time;
 * the returned function reuses it across every call.
 *
 * Use this when you're going to resolve many inputs against the same supported
 * set — e.g. inside `buildI18nextConfig`'s `convertDetectedLanguage` closure
 * (called on every HTTP request), or inside a middleware / request handler that
 * does per-request resolution. For one-off calls, `resolveLocale` is fine.
 *
 * Resolution semantics are identical to `resolveLocale` — see its JSDoc.
 *
 * @example
 * ```ts
 * const resolve = createResolver(['en-US', 'de-DE', 'fr-FR'], 'en-US');
 * resolve('de-AT').locale;  // 'de-DE'
 * resolve('zh-TW').locale;  // 'en-US' (fallback)
 * ```
 *
 * @param supportedLocales - The locales this service has translations for.
 * @param fallbackLocale - The locale to use when no match is found.
 */
/**
 * Precomputed per-supported-locale metadata. Built once at factory time.
 * Each entry carries the derived fields (`base`, `script`, `hasSubtags`) so
 * the returned resolver never redoes the string splits per call.
 *
 * Only `canonical` is exposed on ResolvedLocale.locale — the consumer's raw
 * input string (e.g. `'en_us'`) is deliberately dropped so every returned
 * locale is guaranteed to be canonical BCP 47, matching the LocaleCode
 * contract and i18next's canonicalized supportedLngs downstream.
 */
type SupportedEntry = {
  canonical: string;
  base: string;
  script: string | null;
  hasSubtags: boolean;
};

export function createResolver(
  supportedLocales: readonly string[],
  fallbackLocale: string,
): (input: string | null | undefined) => ResolvedLocale {
  // Precompute both lookup structures ONCE at factory time:
  //
  //   1. `byCanonical` (canonical → SupportedEntry) — O(1) exact-match lookup.
  //   2. `byBase` (base language → SupportedEntry[]) — O(1) narrowing for
  //      sibling / base-language matches; then O(k) within candidates where
  //      k = number of supported locales sharing the same base language,
  //      typically 1-3.
  //
  // Each entry also carries precomputed `base`, `script`, and `hasSubtags`
  // fields so the resolver's per-call work stays constant even as
  // supportedLocales grows (targeting joy-web's planned 50-60 locale set).
  //
  // Array insertion order is preserved inside each `byBase` bucket, so the
  // "first matching sibling wins" contract still holds.
  const byCanonical = new Map<string, SupportedEntry>();
  const byBase = new Map<string, SupportedEntry[]>();
  for (const locale of supportedLocales) {
    const canonical = canonicalizeLocale(locale);
    if (canonical === null) continue;
    const entry: SupportedEntry = {
      canonical,
      base: canonical.split('-')[0],
      script: extractScript(canonical),
      hasSubtags: canonical.includes('-'),
    };
    byCanonical.set(canonical, entry);
    const bucket = byBase.get(entry.base);
    if (bucket) bucket.push(entry);
    else byBase.set(entry.base, [entry]);
  }

  // Also canonicalize the fallback ONCE so consumers passing `'en_us'` or
  // `'EN-US'` get the canonical `'en-US'` in every ResolvedLocale.locale.
  // Otherwise the raw non-canonical fallback flows into i18next's fallbackLng
  // and can mismatch supportedLngs entries downstream (casing/underscore drift).
  const canonicalFallback = (canonicalizeLocale(fallbackLocale) ??
    fallbackLocale) as LocaleCode;

  return (input: string | null | undefined): ResolvedLocale => {
    const rawInput = input ?? null;
    const canonical = canonicalizeLocale(input);

    if (canonical === null) {
      return {
        locale: canonicalFallback,
        recognized: false,
        input: rawInput,
        matchType: 'fallback',
      };
    }

    // 1. Exact match (after canonicalization) — O(1) map lookup.
    const exactMatch = byCanonical.get(canonical);
    if (exactMatch !== undefined) {
      const matchType: MatchType =
        canonical === rawInput ? 'exact' : 'case-normalized';
      return {
        locale: exactMatch.canonical as LocaleCode,
        recognized: true,
        input: rawInput,
        matchType,
      };
    }

    const inputBase = canonical.split('-')[0];
    const inputScript = extractScript(canonical);
    const inputHasSubtags = canonical.includes('-');

    // Narrow to same-base candidates in O(1). Typically 1-3 entries per base.
    const candidates = byBase.get(inputBase);
    if (candidates === undefined) {
      return {
        locale: canonicalFallback,
        recognized: false,
        input: rawInput,
        matchType: 'fallback',
      };
    }

    // 2. Closest sibling — input has extra subtags, find a supported entry
    //    with the same base + extra subtags. Script-scoped: scripts must
    //    match exactly (both null, or both same) so we never serve
    //    Simplified content for a Traditional request or vice versa.
    if (inputHasSubtags) {
      for (const entry of candidates) {
        if (!entry.hasSubtags) continue;
        if (entry.script !== inputScript) continue;
        return {
          locale: entry.canonical as LocaleCode,
          recognized: true,
          input: rawInput,
          matchType: 'closest-sibling',
        };
      }
    }

    // 3. Base-language match — either bare input against regional supported
    //    ('de' → 'de-DE'), or regional input against bare supported. Same
    //    script-scoping applies.
    for (const entry of candidates) {
      if (entry.script !== inputScript) continue;
      // Bare input → regional supported = 'base-language'
      // Regional input → bare supported = 'closest-sibling'
      const matchType: MatchType = inputHasSubtags
        ? 'closest-sibling'
        : 'base-language';
      return {
        locale: entry.canonical as LocaleCode,
        recognized: true,
        input: rawInput,
        matchType,
      };
    }

    // 4. Fallback
    return {
      locale: canonicalFallback,
      recognized: false,
      input: rawInput,
      matchType: 'fallback',
    };
  };
}

/**
 * Resolve an input locale against a set of supported locales. Convenience
 * wrapper for one-off resolutions — internally calls `createResolver` and
 * invokes it once. For repeated resolutions against the same supported set,
 * prefer `createResolver` so the lookup map is built once instead of per call.
 *
 * Resolution order:
 *   1. Exact match (case-insensitive, underscore-normalized)
 *   2. Case-normalized match (input differs only in casing)
 *   3. Closest sibling — same base language, different region (de-AT → de-DE)
 *   4. Base language match — bare code to regional variant (de → de-DE)
 *   5. Fallback locale
 *
 * @param input - Raw locale string from any source (event, browser, payload).
 * @param supportedLocales - The locales this service actually has translations for.
 * @param fallbackLocale - The locale to use when no match is found.
 */
export function resolveLocale(
  input: string | null | undefined,
  supportedLocales: readonly string[],
  fallbackLocale: string,
): ResolvedLocale {
  return createResolver(supportedLocales, fallbackLocale)(input);
}

/**
 * Convenience: resolve event-scoped locale.
 *
 * Same as `resolveLocale` but designed for event-scoped communications
 * (emails, SMS, cards) where the event locale from admin settings is
 * the source of truth.
 *
 * Callers should log a telemetry warning when the result has
 * `recognized === false` AND the input base language differs from
 * the fallback language. That's the production-critical failure:
 * a couple configured a non-English event and guests are getting English.
 *
 * For high-volume per-request resolution (e.g. a backend service resolving
 * event locale on every incoming request), prefer `createResolver` directly
 * so the canonical lookup map is built once instead of on every call.
 *
 * @param eventLocale - The locale from event settings (event_info.locale).
 * @param supportedLocales - The locales this service has translations for.
 * @param fallbackLocale - Defaults to 'en-US'.
 */
export function resolveEventLocale(
  eventLocale: string | null | undefined,
  supportedLocales: readonly string[],
  fallbackLocale: string = 'en-US',
): ResolvedLocale {
  return resolveLocale(eventLocale, supportedLocales, fallbackLocale);
}
